import { Controller } from "@hotwired/stimulus"
import React, { Suspense } from "react"
import { render, unmountComponentAtNode } from "react-dom"
import {
  ErrorBoundary,
  Loading,
  ModalContainer,
  ObjectMapTableSplit,
} from "web/components"
import { BrowserRouter as Router } from "react-router-dom"
import { NavigationProvider } from "web/contexts"
import { AppProviders, LibraryProviders } from "web/Providers"

export default class ObjectMapTableSplitController extends Controller {
  static values = {
    viewGroup: Object,
    viewId: Number,
    subjectType: String,
    itemClickBaseUrls: Object,
    subjectBaseUrl: String,
    addItemUrl: String,
    parentSubjectType: String,
    parentSubjectId: Number,
    enableScheduledExport: Boolean,
    enableCreation: { default: false, type: Boolean },
    enableEdit: { default: true, type: Boolean },
    enableSelection: { default: false, type: Boolean },
    enableExport: { default: true, type: Boolean },
    enableFieldSelection: { default: true, type: Boolean },
    exportPath: String,
    isFavouriteEnabled: { default: true, type: Boolean },
    fetchUrl: String,
    mapFetchUrl: String,
    listTypeFriendlyName: String,
    topic: Object,
    modalTitle: String,
  }

  initialize() {
    global.React = React
  }

  onItemClick(item) {
    this.visitItem(this.subjectTypeValue, item.data.id)
  }

  onMapItemClick(subjectType, id) {
    this.visitItem(subjectType, id)
  }

  visitItem(subjectType, id) {
    const baseUrl = this.itemClickBaseUrlsValue[subjectType]
    if (!baseUrl) return

    window.Turbo.visit(`${baseUrl}/${id}`)
  }

  onAdd() {
    window.Turbo.visit(this.addItemUrlValue, { frame: "modal" })
  }

  disconnect() {
    // Turbo transplants data-turbo-permanent elements during page renders,
    // which briefly disconnects them; only unmount when the element truly
    // left the page.
    requestAnimationFrame(() => {
      if (!this.element.isConnected) {
        unmountComponentAtNode(this.element)
      }
    })
  }

  connect() {
    const parentSubjectType = this.hasParentSubjectTypeValue
      ? this.parentSubjectTypeValue
      : undefined

    const parentSubjectId = this.hasParentSubjectIdValue
      ? this.parentSubjectIdValue
      : undefined

    render(
      <Suspense fallback={<Loading inline />}>
        <Router>
          <LibraryProviders>
            <AppProviders>
              <NavigationProvider
                viewGroup={this.viewGroupValue}
                viewId={this.viewIdValue}
                subjectType={this.subjectTypeValue}
              >
                <ErrorBoundary>
                  <ObjectMapTableSplit
                    subjectType={this.subjectTypeValue}
                    topic={this.hasTopicValue ? this.topicValue : undefined}
                    modalTitle={this.modalTitleValue}
                    enableEdit={this.enableEditValue}
                    enableExport={this.enableExportValue}
                    enableFieldSelection={this.enableFieldSelectionValue}
                    exportPath={this.exportPathValue}
                    onItemClick={
                      this.hasItemClickBaseUrlsValue
                        ? this.onItemClick.bind(this)
                        : undefined
                    }
                    onMapItemClick={
                      this.hasItemClickBaseUrlsValue
                        ? this.onMapItemClick.bind(this)
                        : undefined
                    }
                    subjectBaseUrl={
                      this.hasSubjectBaseUrlValue
                        ? this.subjectBaseUrlValue
                        : undefined
                    }
                    onAdd={this.enableCreationValue && this.onAdd.bind(this)}
                    parentSubjectType={parentSubjectType}
                    parentSubjectId={parentSubjectId}
                    enableScheduledExport={this.enableScheduledExportValue}
                    isFavouriteEnabled={this.isFavouriteEnabledValue}
                    fetchURL={this.fetchUrlValue}
                    mapFetchURL={this.mapFetchUrlValue}
                    listTypeFriendlyName={this.listTypeFriendlyNameValue}
                    enableSelection={this.enableSelectionValue}
                  />
                </ErrorBoundary>
              </NavigationProvider>
              <ModalContainer />
            </AppProviders>
          </LibraryProviders>
        </Router>
      </Suspense>,
      this.element
    )
  }
}
